import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getReaction } from '@peruselab/api-client/reactions';
import { getIsomer } from '@peruselab/api-client/isomers';
import { IsomerThumbnail } from './IsomerThumbnail';
import type { ParticipantRole } from '@peruselab/structure-editor/chemistry/reactionParticipant';

interface ReactionThumbnailProps {
  readonly reactionId: string;
}

interface ResolvedParticipant {
  readonly isomerId: string;
  readonly moleculeId: string;
  readonly role: ParticipantRole;
}

type ReactionThumbnailState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly participants: readonly ResolvedParticipant[] }
  | { readonly status: 'error'; readonly message: string };

const PARTICIPANT_THUMBNAIL_SIZE = 56;

// #139: a read-only rendering of a Reaction Illustration element. Not a reuse of
// the former ReactionComposer.tsx (retired, #705; built for interactive editing -- role buttons, remove, save --
// inappropriate for a passively placed element); this fetches the saved Reaction, resolves each
// participant's Isomer to recover its moleculeId (ReactionParticipant only stores isomerId), and
// renders a compact reactant/product mini-diagram reusing IsomerThumbnail per participant.
// Reagent/catalyst/solvent participants are intentionally omitted from this compact view, per
// #139's scope.
export function ReactionThumbnail({ reactionId }: ReactionThumbnailProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<ReactionThumbnailState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      const reaction = await getReaction(reactionId);
      const participants = await Promise.all(
        reaction.document.participants.map(async (participant) => {
          const isomer = await getIsomer(participant.isomerId);
          return {
            isomerId: participant.isomerId,
            moleculeId: isomer.moleculeId,
            role: participant.role
          };
        })
      );
      if (!cancelled) setState({ status: 'ready', participants });
    })().catch((error: unknown) => {
      if (!cancelled) {
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : t('reactionThumbnail.renderError')
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [reactionId]);

  if (state.status === 'loading') {
    return (
      <div className="reaction-thumbnail">
        <span className="isomer-thumbnail-status">{t('common.loading')}</span>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="reaction-thumbnail">
        <span className="isomer-thumbnail-status isomer-thumbnail-error">{state.message}</span>
      </div>
    );
  }

  const reactants = state.participants.filter((participant) => participant.role === 'reactant');
  const products = state.participants.filter((participant) => participant.role === 'product');

  return (
    <div className="reaction-thumbnail">
      <div className="reaction-thumbnail-column">
        {reactants.map((participant) => (
          <IsomerThumbnail
            key={participant.isomerId}
            moleculeId={participant.moleculeId}
            isomerId={participant.isomerId}
            size={PARTICIPANT_THUMBNAIL_SIZE}
          />
        ))}
      </div>
      <div className="reaction-thumbnail-arrow" aria-hidden="true">
        &#8594;
      </div>
      <div className="reaction-thumbnail-column">
        {products.map((participant) => (
          <IsomerThumbnail
            key={participant.isomerId}
            moleculeId={participant.moleculeId}
            isomerId={participant.isomerId}
            size={PARTICIPANT_THUMBNAIL_SIZE}
          />
        ))}
      </div>
    </div>
  );
}
